import React, { FC } from 'react';
import Button from '../ui/Button';
import IconSvg from '../ui/IconSvg';
import { getActions } from '../../global';
import {
  ERoutes,
  LeftColumnContent,
  MiddleColumnContent,
  SettingsScreens,
} from '../../types';
import { useTranslation } from 'react-i18next';
import ListItem from '../ui/ListItem';
import IconSvgSettings from './settings/icons/IconSvgSettings';
import useLastCallback from '../../hooks/useLastCallback';
import useHistoryBack from '../../hooks/useHistoryBack';
import { SUPPORT_USER_ID } from '../../config';

const QuickLinks: FC = () => {
  const { toggleLeftColumn, setMenuShow, openChatByUsername, openChat } =
    getActions();
  const { t } = useTranslation();

  const { toggleScreens } = useHistoryBack();

  function selectLink({
    leftScreen,
    middleScreen,
    settingsScreen,
    pathname,
    isToggleLeft,
  }: {
    leftScreen: LeftColumnContent;
    middleScreen?: MiddleColumnContent;
    settingsScreen?: SettingsScreens;
    pathname?: ERoutes;
    isToggleLeft?: boolean;
  }) {
    toggleScreens({
      leftScreen,
      middleScreen,
      settingScreen: settingsScreen,
      pathname,
    });

    isToggleLeft && toggleLeftColumn();
  }

  const handleSelectContacts = useLastCallback(() => {
    selectLink({
      leftScreen: LeftColumnContent.Contacts,
      pathname: ERoutes.contacts,
      isToggleLeft: true,
    });
  });

  const handleSelectBuyCredits = useLastCallback(() => {
    selectLink({
      leftScreen: LeftColumnContent.Settings,
      middleScreen: MiddleColumnContent.Settings,
      settingsScreen: SettingsScreens.Wallet,
      pathname: ERoutes.profile,
    });
  });

  const handleSelectEarn = useLastCallback(() => {
    selectLink({
      leftScreen: LeftColumnContent.Settings,
      middleScreen: MiddleColumnContent.Settings,
      settingsScreen: SettingsScreens.LoyaltyProgram,
      pathname: ERoutes.profile,
    });
  });

  const handleSelectAiSpace = useLastCallback(() => {
    selectLink({
      leftScreen: LeftColumnContent.Settings,
      middleScreen: MiddleColumnContent.Settings,
      settingsScreen: SettingsScreens.aiSpace,
      pathname: ERoutes.aiSpace,
    });
  });

  const handleSelectCreate = useLastCallback(() => {
    selectLink({
      leftScreen: LeftColumnContent.ChatList,
      isToggleLeft: true,
    });
    setMenuShow(true);
  });

  const handleSelectTips = useLastCallback(() => {
    openChatByUsername({ username: 'ellotips' });
  });

  const handleSelectSupport = useLastCallback(() => {
    openChat({ id: SUPPORT_USER_ID });
  });

  const onReset = () => {
    toggleScreens({
      leftScreen: LeftColumnContent.ChatList,
      middleScreen: MiddleColumnContent.Messages,
    });
  };

  return (
    <div id='QuickLinks' className='settings-layout'>
      <div className='MiddleHeader'>
        <div className='middle-header-inner'>
          <div className='setting-info'>
            <Button
              round
              size='smaller'
              color='translucent'
              onClick={onReset}
              ariaLabel={String(t('GoBack'))}
            >
              <i className='icon-svg'>
                <IconSvg name='arrow-left' />
              </i>
            </Button>
            <h4>Quick links</h4>
          </div>
        </div>
      </div>
      <div className='settings-content custom-scroll'>
        <div className='settings-container quick-links-container'>
          <div className='links-item-middle'>
            <ListItem
              leftElement={
                <div className='title-icon tips-bg mr-3'>
                  <IconSvg name='lamp-charge' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectTips}
            >
              <div className='info'>
                <div className='info-row'>Tips</div>
                <div className='sub'>
                  Elevate your Ello experience with proven tips and tricks for
                  channels, groups or AI bot.
                </div>
              </div>
            </ListItem>
            <ListItem
              leftElement={
                <div className='title-icon earn-bg mr-3'>
                  <IconSvg name='user-plus' w='20' h='20' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectEarn}
            >
              <div className='info'>
                <div className='info-row'>Earn Instantly</div>
                <div className='sub'>
                  Invite friends to join and both of you will receive $5 for
                  channel subscriptions community groups, online courses, and
                  engaging with AI.
                </div>
              </div>
            </ListItem>

            <ListItem
              leftElement={
                <div className='title-icon contacts-bg mr-3'>
                  <IconSvg name='search' w='18' h='18' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectContacts}
            >
              <div className='info'>
                <div className='info-row'>Contacts</div>
                <div className='sub'>
                  View all your contacts or use the search function to find any
                  user, channel, group or AI bot.
                </div>
              </div>
            </ListItem>
            <ListItem
              leftElement={
                <div className='title-icon broadcast-bg mr-3'>
                  <IconSvg name='people' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectCreate}
            >
              <div className='info'>
                <div className='info-row'>Broadcast</div>
                <div className='sub'>
                  Instantly create channels, online courses and community
                  groups.
                </div>
              </div>
            </ListItem>
            <ListItem
              leftElement={
                <div className='title-icon pay-bg mr-3'>
                  <IconSvg name='dollar' w='18' h='18' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectBuyCredits}
            >
              <div className='info'>
                <div className='info-row'>Ello Pay</div>
                <div className='sub'>
                  All your financial transactions in one secure location.
                </div>
              </div>
            </ListItem>

            <ListItem
              leftElement={
                <div className='title-icon mr-3'>
                  <IconSvgSettings name='ai-bot' w='16' h='16' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectAiSpace}
            >
              <div className='info'>
                <div className='info-row'>AI Space</div>
                <div className='sub'>Discover the exciting world of AI.</div>
              </div>
            </ListItem>
            <ListItem
              leftElement={
                <div className='title-icon color-bg-13 mr-3'>
                  <IconSvg name='support' w='16' h='16' />
                </div>
              }
              rightElement={<i className='css-icon-right'></i>}
              onClick={handleSelectSupport}
            >
              <div className='info'>
                <div className='info-row'>Support</div>
                <div className='sub'>
                  Ask a question, report a bug, or suggest an idea.
                </div>
              </div>
            </ListItem>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickLinks;
